import * as html from 'smg/html';
import { GetIconLibrary } from 'smg/icons';
import { BindToastActions, NewToastLifetime, SetCustomRenderer, ToastLifetime, ToastOptions } from 'smg/toast';

const TOAST_DURATION = 4000;

let $stack: HTMLElement;

function getStack(): HTMLElement {
    if (!$stack) {
        $stack = document.createElement('div');
        $stack.className = 'toast-stack';
        document.body.appendChild($stack);
    }
    return $stack;
}

function icon(name: string, color: string): string {
    const style = `font-size:12px;color:${color}`;
    switch (GetIconLibrary()) {
    case 'fontAwesome4':
        return `<i class="fa fa-${name}" style="${style}"></i>`;
    case 'fontAwesome5':
        return `<i class="fas fa-${name}" style="${style}"></i>`;
    case 'materialSymbolsRounded':
        return `<span class="material-symbols-rounded" style="${style}">${name}</span>`;
    case 'materialSymbolsSharp':
        return `<span class="material-symbols-sharp" style="${style}">${name}</span>`;
    case 'bootstrapIcons':
        return `<i class="bi bi-${name}" style="${style}"></i>`;
    }
}

let toastCounter = 0;

function dismiss(el: HTMLElement): Promise<void> {
    el.classList.add('toast-hide');
    return new Promise(resolve => {
        setTimeout(() => {
            el.remove();
            resolve();
        }, 250);
    });
}

// showToast mounts the toast under smg/toast's lifetime: the X, the countdown
// (pauses under the pointer and while a command runs, the progress bar with
// it), a finished command and the handle all go through one hide, and the
// lifetime ends once the fade has removed the element.
function showToast(el: HTMLElement, autoDismiss: boolean): ToastLifetime {
    const stack = getStack();
    const bar = el.querySelector('.toast-progress-bar') as HTMLElement | null;
    const life = NewToastLifetime({
        $toast: el,
        delay: autoDismiss ? TOAST_DURATION : false,
        close: () => {
            void dismiss(el).then(life.end);
        },
        onCounting: counting => {
            if (bar) {
                bar.style.animationPlayState = counting ? 'running' : 'paused';
            }
        },
    });
    const $dismiss = el.querySelector('.toast-dismiss') as HTMLButtonElement;
    $dismiss.addEventListener('click', life.handle.hide);

    stack.appendChild(el);

    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            el.classList.add('toast-show');
            if (bar && autoDismiss) {
                bar.style.setProperty('--toast-duration', `${TOAST_DURATION}ms`);
                bar.classList.add('toast-progress-animate');
            }
        });
    });

    return life;
}

const iconConfig: Record<string, { name: string; color: string; cssClass: string }> = {
    success: { name: 'check', color: '#3B6D11', cssClass: '' },
    info: { name: 'undo', color: '#495057', cssClass: ' toast-check-undo' },
    warning: { name: 'undo', color: '#495057', cssClass: ' toast-check-undo' },
    error: { name: 'times', color: '#e24b4a', cssClass: ' toast-check-error' },
};

function customRenderer(opts: ToastOptions) {
    const id = ++toastCounter;
    const cfg = iconConfig[opts.type] || iconConfig.success;
    const autoDismiss = opts.type !== 'error';

    const actions = opts.actions ?? [];
    let actionsHTML = '';
    if (actions.length) {
        const items = actions.map(action => {
            const label = html.Escape(action.label);
            if (action.href !== undefined) {
                return `<a class="toast-action-link">${label}</a>`;
            }
            return `<button type="button" class="toast-action-link">${label}</button>`;
        }).join('');
        actionsHTML = `<div class="toast-sub d-flex flex-wrap" style="gap:1rem">${items}</div>`;
    }

    const el = document.createElement('div');
    el.className = 'toast-item';
    el.id = `toast-${id}`;
    el.innerHTML = `
        <div class="toast-check${cfg.cssClass}">${icon(cfg.name, cfg.color)}</div>
        <div class="toast-body">
            <div class="toast-title">${opts.message}</div>
            ${actionsHTML}
        </div>
        <button class="toast-dismiss">&times;</button>
        <div class="toast-progress${opts.type === 'error' ? ' toast-progress-error' : ''}">
            <div class="toast-progress-bar" id="toast-bar-${id}"></div>
        </div>`;

    const life = showToast(el, autoDismiss);
    BindToastActions(el.querySelectorAll('.toast-action-link'), actions, life.host);
    return life.handle;
}

export function Init() {
    SetCustomRenderer(customRenderer);
}
